<script setup lang="ts">
import { FormCheck, FormInput, FormLabel } from "@/components/Base/Form";
import Tippy from "@/components/Base/Tippy";
import Button from "@/components/Base/Button";
import { Dialog } from "@/components/Base/Headless"; 
import { ref, reactive } from "vue";
import { useRouter } from "vue-router";
import { useI18n } from "vue-i18n";
import axios from "@/utils/axios";
import Toastify from "toastify-js";
import CountrySelect from "@/components/Form/CountrySelect.vue";
import LanguageSwitcher from "@/components/LanguageSwitcher.vue";

const { t } = useI18n();
const router = useRouter();
const isLoading = ref(false);
const success = ref(false);

const form = reactive({
  email: "",
  first_name: "",
  last_name: "",
  dni: "",
  country: "",
  phone: "",
});

const handleRegister = async () => {
  isLoading.value = true;
  try {
    // Only send what the API expects for initial registration
    await axios.post("/auth/register", form);
    
    // Show success message inline
    success.value = true;

  } catch (error: any) {
    if (error.response?.status === 409) {
        Toastify({
          text: t('auth.already_registered_toast') + ` <a href="/forgot-password" style="font-weight: bold; text-decoration: underline; margin-left: 10px;">${t('auth.recover_password')}</a>`,
          escapeMarkup: false,
          duration: 5000,
          gravity: "top",
          position: "center",
          style: {
            background: "#eab308",
            color: "#fff"
          },
        }).showToast();
    } else {
        Toastify({
          text: error.response?.data?.message || t('auth.error_registration'),
          duration: 3000,
          gravity: "top",
          position: "center",
          style: {
            background: "#ef4444",
          },
        }).showToast();
    }
  } finally {
    isLoading.value = false;
  }
};
</script>

<template>
  <div
    class="container grid lg:h-screen grid-cols-12 lg:max-w-[1550px] 2xl:max-w-[1750px] py-10 px-5 sm:py-14 sm:px-10 md:px-36 lg:py-0 lg:pl-14 lg:pr-12 xl:px-24"
  >
    <div
      :class="[
        'relative z-50 h-full col-span-12 p-7 sm:p-14 bg-white rounded-2xl lg:bg-transparent lg:pr-10 lg:col-span-5 xl:pr-24 2xl:col-span-4 lg:p-0 dark:bg-darkmode-600',
        'before:content-[\'\'] before:absolute before:inset-0 before:-mb-3.5 before:bg-white/40 before:rounded-2xl before:mx-5 dark:before:hidden',
      ]"
    >
      <div
        class="relative z-10 flex flex-col justify-center w-full h-full py-2 lg:py-32"
      >
        <div class="absolute top-2 right-0">
             <LanguageSwitcher placement="bottom-end" />
        </div>
        <div class="flex items-center justify-center">
          <img alt="Logo" class="w-16" src="@/assets/images/logo.svg" />
        </div>
        <div class="mt-10">
          <div class="text-2xl font-medium">{{ $t('auth.sign_up') }}</div>
          
          <div v-if="success" class="mt-10 p-8 text-center bg-green-50 rounded-xl border border-green-200">
             <h2 class="text-2xl font-semibold text-green-700 mb-4">{{ $t('auth.register_success_title') }}</h2>
             <p class="text-lg text-green-800 leading-relaxed">
               {{ $t('auth.register_success_msg') }}
             </p>
             <div class="mt-8">
               <router-link to="/login">
                 <Button variant="primary" class="w-full sm:w-auto px-8 py-3">{{ $t('auth.back_to_login') }}</Button>
               </router-link>
             </div>
          </div>
          <form v-else @submit.prevent="handleRegister" class="mt-6">
            <FormLabel>{{ $t('auth.first_name') }}*</FormLabel>
            <FormInput
              type="text"
              class="block px-4 py-3.5 rounded-[0.6rem] border-slate-300/80"
              :placeholder="$t('auth.first_name')"
              v-model="form.first_name"
              required
            />
            <FormLabel class="mt-5">{{ $t('auth.last_name') }}*</FormLabel>
            <FormInput
              type="text"
              class="block px-4 py-3.5 rounded-[0.6rem] border-slate-300/80"
              :placeholder="$t('auth.last_name')"
              v-model="form.last_name"
              required
            />

            <FormLabel class="mt-5">{{ $t('auth.dni') }}*</FormLabel>
            <FormInput
              type="text"
              class="block px-4 py-3.5 rounded-[0.6rem] border-slate-300/80"
              :placeholder="$t('auth.dni')"
              v-model="form.dni"
              required
            />
            
            <FormLabel class="mt-5">{{ $t('auth.phone') }}*</FormLabel>
            <FormInput
              type="tel"
              class="block px-4 py-3.5 rounded-[0.6rem] border-slate-300/80"
              :placeholder="$t('auth.phone')"
              v-model="form.phone"
              required
            />
            
            <FormLabel class="mt-5">{{ $t('auth.nationality') }}*</FormLabel>
            <CountrySelect v-model="form.country" class="mt-2" required />
            
            <FormLabel class="mt-5">{{ $t('auth.email') }}*</FormLabel>
            <FormInput
              type="email"
              class="block px-4 py-3.5 rounded-[0.6rem] border-slate-300/80"
              :placeholder="$t('auth.email')"
              v-model="form.email"
              required
            />
            
            <div
              class="flex items-center mt-5 text-xs text-slate-500 sm:text-sm"
            >
              <FormCheck.Input
                id="remember-me"
                type="checkbox"
                class="mr-2 border"
                required
              />
              <label class="cursor-pointer select-none" htmlFor="remember-me">
                {{ $t('auth.agree_policy') }}
              </label>
              <a class="ml-1 text-primary dark:text-slate-200" href="">
                {{ $t('auth.privacy_policy') }}
              </a>
              .
            </div>
            <div class="mt-5 text-center xl:mt-8 xl:text-left">
              <Button
                variant="primary"
                rounded
                type="submit"
                class="bg-gradient-to-r from-theme-1/70 to-theme-2/70 w-full py-3.5 xl:mr-3 dark:border-darkmode-400"
                :disabled="isLoading"
              >
                <!-- Loading spinner support -->
                <svg v-if="isLoading" class="w-5 h-5 mr-3 -ml-1 text-white animate-spin inline" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                {{ isLoading ? '' : $t('auth.sign_up') }}
              </Button>
            </div>
            <div class="mt-5 text-slate-600 dark:text-slate-400 text-center">
              {{ $t('auth.already_have_account') }}
              <router-link class="font-medium text-primary" to="/login"> {{ $t('auth.sign_in') }} </router-link>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
  <div
    class="fixed container grid w-screen inset-0 h-screen grid-cols-12 lg:max-w-[1550px] 2xl:max-w-[1750px] pl-14 pr-12 xl:px-24"
  >
    <!-- Background Image Divs -->
     <div
      :class="[
        'relative h-screen col-span-12 lg:col-span-5 2xl:col-span-4 z-20',
        'after:bg-white after:hidden after:lg:block after:content-[\'\'] after:absolute after:right-0 after:inset-y-0 after:bg-gradient-to-b after:from-white after:to-slate-100/80 after:w-[800%] after:rounded-[0_1.2rem_1.2rem_0/0_1.7rem_1.7rem_0] dark:after:bg-darkmode-600 dark:after:from-darkmode-600 dark:after:to-darkmode-600',
        'before:content-[\'\'] before:hidden before:lg:block before:absolute before:right-0 before:inset-y-0 before:my-6 before:bg-gradient-to-b before:from-white/10 before:to-slate-50/10 before:bg-white/50 before:w-[800%] before:-mr-4 before:rounded-[0_1.2rem_1.2rem_0/0_1.7rem_1.7rem_0] dark:before:from-darkmode-300 dark:before:to-darkmode-300',
      ]"
    ></div>
    <div
      :class="[
        'h-full col-span-7 2xl:col-span-8 lg:relative',
        'before:content-[\'\'] before:absolute before:lg:-ml-10 before:left-0 before:inset-y-0 before:bg-gradient-to-b before:from-theme-1 before:to-theme-2 before:w-screen before:lg:w-[800%]',
        'after:content-[\'\'] after:absolute after:inset-y-0 after:left-0 after:w-screen after:lg:w-[800%] after:bg-texture-white after:bg-fixed after:bg-center after:lg:bg-[25rem_-25rem] after:bg-no-repeat',
      ]"
    >
      <div
        class="sticky top-0 z-10 flex-col justify-center hidden h-screen ml-16 lg:flex xl:ml-28 2xl:ml-36"
      >
        <div
          class="leading-[1.4] text-[2.6rem] xl:text-5xl font-medium xl:leading-[1.2] text-white"
        >
          <br />
        </div>
        <div class="mt-5 text-base leading-relaxed xl:text-lg text-white/70">
        </div>
      </div>
    </div>
  </div>


</template>
